// src/components/presets/BannerDefault.tsx
'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X, Check } from 'lucide-react'
import type { BannerProps } from '../Banner'
import { getAnimationValues, getBorderRadiusValue } from '../../utils/style-helpers'
import { getCombinedStyles } from '../../utils/positioning'
import { getCategoryLabel, getCategoryShortDescription } from '../../utils/translation-helpers'

function CategoryCheckbox({
  checked,
  disabled,
  onChange,
  label,
  description,
  accentColor,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: () => void
  label: string
  description: string
  accentColor: string
}) {
  const id = React.useId()

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        padding: '12px',
        backgroundColor: checked ? `${accentColor}08` : '#f9fafb',
        borderRadius: '8px',
        border: checked ? `1px solid ${accentColor}30` : '1px solid #e5e7eb',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s',
      }}
    >
      <div style={{ position: 'relative', marginTop: '2px' }}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{ position: 'absolute', opacity: 0, width: '1px', height: '1px' }}
        />
        <label
          htmlFor={id}
          style={{ display: 'block', cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <div
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '4px',
              border: checked ? `2px solid ${accentColor}` : '2px solid #d1d5db',
              backgroundColor: checked ? accentColor : '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            {checked && (
              <Check style={{ width: '12px', height: '12px', color: '#ffffff', strokeWidth: 3 }} />
            )}
          </div>
        </label>
      </div>
      <label htmlFor={id} style={{ flex: 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
          {label}
          {disabled && (
            <span
              style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400, marginLeft: '6px' }}
            >
              (vždy aktivní)
            </span>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>{description}</div>
      </label>
    </div>
  )
}

export function BannerDefault(props: BannerProps) {
  const {
    config,
    texts,
    categories,
    consent,
    onAccept,
    onDecline,
    onClose,
    onConsentChange,
    accentColor,
    style,
  } = props
  const {
    closeButton,
    showSettingsButton,
    showRejectLink,
    descriptionMode = 'short',
    language = 'en',
  } = config
  const [showSettings, setShowSettings] = useState(false)

  const animationValues = getAnimationValues(style.animation.direction)
  const description =
    descriptionMode === 'long'
      ? texts.longDescription || texts.description
      : texts.shortDescription || texts.description

  const containerStyles = getCombinedStyles(style.position, style.maxWidth, {
    backgroundColor: style.background,
    borderRadius: getBorderRadiusValue(style.borderRadius),
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e5e7eb',
  })

  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '8px'

  const handleSaveSettings = () => {
    setShowSettings(false)
    onAccept()
  }

  return (
    <motion.div
      initial={style.animation.enabled ? animationValues.initial : { opacity: 1 }}
      animate={style.animation.enabled ? animationValues.animate : { opacity: 1 }}
      exit={style.animation.enabled ? animationValues.exit : { opacity: 0 }}
      transition={{ duration: style.animation.duration }}
      style={containerStyles}
    >
      <div style={{ position: 'relative', padding: '24px', overflow: 'hidden' }}>
        {closeButton && (
          <button
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              onClose()
            }}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              zIndex: 10,
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label={texts.closeButton}
          >
            <X style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
          </button>
        )}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: `${accentColor}15`,
            }}
          >
            <Cookie style={{ width: '24px', height: '24px', color: accentColor }} />
          </div>
          <div style={{ flex: 1, paddingRight: '24px' }}>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '8px',
                lineHeight: 1.4,
              }}
            >
              {texts.heading}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
              {description}
            </p>
          </div>
        </div>

        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', marginBottom: '16px' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories.map(category => (
                  <CategoryCheckbox
                    key={category.id}
                    checked={consent[category.id] !== false}
                    disabled={category.required}
                    onChange={() =>
                      !category.required && onConsentChange?.(category.id, !consent[category.id])
                    }
                    label={getCategoryLabel(category, language)}
                    description={getCategoryShortDescription(category, language)}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={showSettings ? handleSaveSettings : onAccept}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: accentColor,
              border: 'none',
              borderRadius: buttonBorderRadiusValue,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            {showSettings ? texts.saveButton : texts.acceptButton}
          </button>

          {showSettingsButton && (
            <button
              onClick={() => setShowSettings(!showSettings)}
              style={{
                width: '100%',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                color: showSettings ? accentColor : '#374151',
                backgroundColor: 'transparent',
                border: showSettings ? `2px solid ${accentColor}` : '2px solid #e5e7eb',
                borderRadius: buttonBorderRadiusValue,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.borderColor = showSettings ? accentColor : '#d1d5db')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.borderColor = showSettings ? accentColor : '#e5e7eb')
              }
            >
              {showSettings ? texts.cancelButton : texts.settingsButton}
            </button>
          )}

          {showRejectLink && !showSettings && (
            <button
              onClick={onDecline}
              style={{
                width: '100%',
                textAlign: 'center',
                fontSize: '14px',
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
            >
              {texts.rejectLink || texts.declineButton}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
